import { useEffect, useState } from "react";
import { useUser, SignUpButton } from "@clerk/clerk-react";
import { ArrowRight, ChevronDown } from "lucide-react";
import Button from "../ui/Button";
import HeroHandlePicker from "../HeroHandlePicker";

// Variant A — Editorial / quiet luxury.
// Single-column reading flow, oversized serif, heavy negative space,
// drop-cap section starts. Designed to feel like a magazine essay rather
// than a SaaS landing.
const FAQ: { q: string; a: string }[] = [
  { q: "Do I need an app?", a: "No. The recipient just opens the link." },
  { q: "What if my title changes?", a: "Update the card. Every share updates with it." },
  { q: "Is there a free tier?", a: "Yes. One card, five links, basic analytics. Forever." },
  { q: "Does the physical card need power?", a: "No. NFC chips are passive — they draw energy from the phone." },
];

export default function LandingEditorial() {
  const { isSignedIn } = useUser();
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  useEffect(() => {
    document.title = "WhoBrew — A quieter way to share what you do";
  }, []);

  return (
    <div className="bg-[#fafaf7] text-coffee-900">
      {/* HERO — quiet, oversized */}
      <section className="relative pt-24 sm:pt-32 pb-20 sm:pb-28">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-[11px] tracking-eyebrow text-coffee-500 mb-8 font-semibold">
            Issue 01 · Spring 2026
          </p>
          <h1 className="text-display text-[56px] sm:text-[88px] md:text-[120px] leading-[0.92] mb-10">
            A quieter way
            <br />
            <em className="text-coffee-700 not-italic font-normal italic">to share</em>
            <br />
            what you do.
          </h1>
          <p className="text-xl sm:text-2xl text-coffee-700 leading-relaxed font-serif italic max-w-xl">
            One link. Your name. Your work. Updated when you change, never when you don't.
          </p>
          <div className="mt-10 max-w-md">
            <HeroHandlePicker origin={typeof window !== "undefined" ? window.location.origin : "https://whobrew.us"} />
          </div>
        </div>
      </section>

      {/* DROP-CAP STORY */}
      <section className="py-20 sm:py-28 border-t border-coffee-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <p className="text-[11px] tracking-eyebrow text-coffee-500 mb-6 font-semibold">
            What we believe
          </p>
          <p className="text-coffee-800 text-lg sm:text-xl leading-[1.7]">
            <span className="float-left text-[88px] leading-[0.85] mr-3 mt-1.5 font-serif text-coffee-900 font-semibold">P</span>
            aper business cards have been the same since 1840. They get a new font every decade and
            people keep printing them, even though half of every box ends up in a drawer that
            nobody opens. We thought: if you only need one card, and the link can change but the
            card cannot, then what you really wanted was a URL.
          </p>
          <p className="mt-6 text-coffee-800 text-lg sm:text-xl leading-[1.7]">
            That's all WhoBrew is. A URL that can be your name. A page that can be everything you
            currently paste into a thirty-second email reply. An NFC card if you want one.
          </p>
        </div>
      </section>

      {/* THREE PILLARS */}
      <section className="py-20 sm:py-28 border-t border-coffee-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-[11px] tracking-eyebrow text-coffee-500 mb-8 font-semibold">
            The whole product
          </p>
          {[
            {
              num: "I",
              title: "A card that changes.",
              body: "Update your title once. The link stays the same. Every business card you ever shared updates with it.",
            },
            {
              num: "II",
              title: "Links instead of letters.",
              body: "LinkedIn. Calendar. Portfolio. Resume. The seven things people actually ask for, in one link.",
            },
            {
              num: "III",
              title: "An NFC card, if you want one.",
              body: "$14.99, one-time, optional. A thin PVC card with a chip. Tap it to a phone. The card opens.",
            },
          ].map((p) => (
            <div key={p.num} className="grid grid-cols-12 gap-6 py-10 border-b border-coffee-200 last:border-0">
              <div className="col-span-2 text-display text-3xl sm:text-4xl text-coffee-400">{p.num}</div>
              <div className="col-span-10">
                <h3 className="text-display text-2xl sm:text-3xl text-coffee-900">{p.title}</h3>
                <p className="mt-3 text-coffee-700 text-lg leading-relaxed">{p.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING */}
      <section className="py-20 sm:py-28 border-t border-coffee-200">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <p className="text-[11px] tracking-eyebrow text-coffee-500 mb-8 font-semibold">Plans</p>
          <div className="space-y-1">
            {[
              { name: "Starter", price: "Free", note: "1 card · 5 links · basic analytics" },
              { name: "Pro", price: "$4.99/mo", note: "3 cards · 10 links · click tracking · custom themes" },
              { name: "Physical NFC", price: "$14.99", note: "PVC card with chip + printed QR backup, one-time" },
            ].map((p) => (
              <div key={p.name} className="flex items-baseline gap-6 py-5 border-b border-coffee-200">
                <div className="text-display text-xl text-coffee-900 w-32">{p.name}</div>
                <div className="text-display text-2xl text-coffee-700 w-32">{p.price}</div>
                <div className="text-sm text-coffee-600 flex-1">{p.note}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 sm:py-28 border-t border-coffee-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <p className="text-[11px] tracking-eyebrow text-coffee-500 mb-8 font-semibold">Questions</p>
          <div className="divide-y divide-coffee-200">
            {FAQ.map((item, i) => {
              const open = openFAQ === i;
              return (
                <div key={i}>
                  <button
                    onClick={() => setOpenFAQ(open ? null : i)}
                    className="w-full text-left py-5 flex items-center justify-between"
                    aria-expanded={open}
                  >
                    <span className="font-serif text-xl text-coffee-900">{item.q}</span>
                    <ChevronDown className={`w-5 h-5 text-coffee-500 transition-transform ${open ? "rotate-180" : ""}`} />
                  </button>
                  {open && (
                    <p className="text-coffee-700 leading-relaxed pb-5 max-w-md">{item.a}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL */}
      <section className="py-32 sm:py-40 border-t border-coffee-200">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-display text-5xl sm:text-7xl text-coffee-900 leading-[0.95]">
            Get yours.
          </h2>
          <p className="mt-6 text-xl text-coffee-700 font-serif italic">
            Sixty seconds. Free. No card to reprint.
          </p>
          <div className="mt-10">
            {isSignedIn ? (
              <Button to="/my-card" size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                Open my card
              </Button>
            ) : (
              <SignUpButton mode="modal">
                <Button size="lg" trailingIcon={<ArrowRight className="w-4 h-4" />}>
                  Claim my link
                </Button>
              </SignUpButton>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
